import type { BadgeProps } from '@/components/ui/badge'

/**
 * Domain state → Badge variant.
 *
 * These maps exist so that a given state looks identical wherever it is shown.
 * Before them, each screen carried its own `getStatusClasses()` returning raw
 * class triplets, and the same state drifted apart between screens — a snag was
 * `bg-orange-50` in one list and `bg-amber-50` in another, "closed" was three
 * different greys.
 *
 * Adding a state: map it to an existing variant. Reach for a NEW variant only
 * when the state is genuinely a different signal, and add it to `badgeVariants`
 * in components/ui/badge.tsx — never to a call site.
 */
export type StatusVariant = NonNullable<BadgeProps['variant']>

const FALLBACK: StatusVariant = 'neutral'

const lookup = <T extends string>(map: Record<T, StatusVariant>) =>
  (key: string | null | undefined): StatusVariant =>
    (key && (map as Record<string, StatusVariant>)[key]) || FALLBACK

/** Snag lifecycle. REOPENED shares `warning` with IN_PROGRESS: both mean "needs attention". */
export const snagStatusVariant = lookup({
  OPEN: 'danger',
  IN_PROGRESS: 'warning',
  REOPENED: 'warning',
  RESOLVED: 'success',
  VERIFIED: 'info',
  CLOSED: 'neutral',
})

export const snagPriorityVariant = lookup({
  LOW: 'neutral',
  MEDIUM: 'info',
  HIGH: 'warning',
  CRITICAL: 'danger',
})

/**
 * Tender lifecycle (Prisma TenderStatus). The approval round-trips share
 * `warning` — each means "someone is on the hook to act".
 */
export const tenderStatusVariant = lookup({
  PREPARATION: 'info',
  ANOTHER_ROUND: 'warning',
  HOD_APPROVAL: 'warning',
  HOD_REVISE: 'warning',
  CLIENT_APPROVAL: 'warning',
  CLIENT_REVISE: 'warning',
  APPROVED: 'success',
  RUNNING: 'info',
  AWARDED: 'success',
  CLOSED: 'neutral',
  ARCHIVED: 'neutral',
})

/** Meeting occurrence lifecycle. COMPLETED is the MeetingStatus enum value; FINALIZED is the occurrence-level wording for the same end state. */
export const meetingStatusVariant = lookup({
  SCHEDULED: 'info',
  IN_PROGRESS: 'warning',
  COMPLETED: 'success',
  FINALIZED: 'success',
  CANCELLED: 'neutral',
})

/**
 * Meeting type (MeetingType enum) — a category, not a lifecycle, but it is drawn
 * with the same tinted badge, so it shares the vocabulary. TECHNICAL was purple,
 * which is outside the variant set; it is `neutral` because the other three
 * already own the signal colours and "technical" carries no urgency.
 */
export const meetingTypeVariant = lookup({
  PROGRESS: 'info',
  SAFETY: 'danger',
  CLIENT: 'success',
  TECHNICAL: 'neutral',
})

/** Agenda sub-item kind. TASK is not in the Prisma enum (yet) but is rendered defensively. */
export const agendaSubItemKindVariant = lookup({
  NOTE: 'neutral',
  DECISION: 'info',
  RISK: 'danger',
  TASK: 'warning',
})

/** Meeting attendance. Absent is `neutral`, not `danger` — not attending is not an error. */
export const attendancePresenceVariant = (present: boolean | null | undefined): StatusVariant =>
  present ? 'success' : 'neutral'

/** Action item lifecycle inside meeting minutes (Prisma ActionItemStatus). */
export const actionItemStatusVariant = lookup({
  OPEN: 'danger',
  IN_PROGRESS: 'warning',
  RESOLVED: 'success',
  CLOSED: 'neutral',
})

/**
 * Doc Control transmittal lifecycle. The Prisma enum is DRAFT | SENT | EXPIRED;
 * ACKNOWLEDGED / VIEWED are the "recipient acted on it" signal the list screen
 * still renders, kept here so both spellings land on the same green.
 */
export const transmittalStatusVariant = lookup({
  DRAFT: 'neutral',
  SENT: 'info',
  VIEWED: 'success',
  ACKNOWLEDGED: 'success',
  EXPIRED: 'danger',
  VOID: 'neutral',
})

/** AI processing job lifecycle (Prisma AiJobStatus). */
export const aiJobStatusVariant = lookup({
  PENDING: 'warning',
  PROCESSING: 'info',
  NEEDS_REVIEW: 'warning',
  COMPLETED: 'success',
  FAILED: 'danger',
  CANCELLED: 'neutral',
})

/** Job log severity. INFO is not rendered as a badge, so it has no entry. */
export const logLevelVariant = lookup({
  ERROR: 'danger',
  WARN: 'warning',
})

/**
 * Doc Control activity actions. The enum has ~30 members that group into five
 * signals, so this matches on substrings in order — first match wins, exactly as
 * the Activity Log has always done. SEND keeps the brand tint at its call site
 * and therefore deliberately falls through to `outline`.
 */
export const docActivityVariant = (action: string): StatusVariant => {
  if (action.includes('CREATE') || action.includes('UPLOAD') || action.includes('GRANT')) return 'success'
  if (action.includes('DELETE') || action.includes('REVOKE')) return 'danger'
  if (action.includes('UPDATE') || action.includes('MOVE') || action.includes('RESTORE') || action.includes('RENAME')) return 'warning'
  if (action.includes('VIEW') || action.includes('DOWNLOAD') || action.includes('ACCESS') || action.includes('PREVIEW')) return 'info'
  return 'outline'
}

/**
 * AI extraction confidence. Thresholds, not an enum — the boundaries live here so
 * every AI screen draws the same line between "trust it" and "check it".
 */
export const confidenceVariant = (confidence: number): StatusVariant =>
  confidence >= 0.9 ? 'success' : confidence >= 0.7 ? 'warning' : 'danger'

/**
 * The same signal as `confidenceVariant`, but at the looser boundaries the Excel
 * extraction preview has always used (0.8 / 0.5 instead of 0.9 / 0.7). It is kept
 * separate on purpose: the preview pairs the colour with a high/medium/low LABEL
 * drawn at these very boundaries, so shifting the colour alone would let the badge
 * read "high" in amber. Unify the two only together with `getConfidenceLabel` in
 * AiExtractionPreview.
 */
export const extractionConfidenceVariant = (confidence: number): StatusVariant =>
  confidence >= 0.8 ? 'success' : confidence >= 0.5 ? 'warning' : 'danger'

/** AI match confidence when the API reports it as a level rather than a number. */
export const confidenceLevelVariant = lookup({
  HIGH: 'success',
  MEDIUM: 'warning',
  LOW: 'danger',
})

/** Submittal lifecycle. OPEN/DRAFT are informational; both review round-trips are "someone must act". */
export const submittalStatusVariant = lookup({
  DRAFT: 'info',
  OPEN: 'info',
  UNDER_REVIEW: 'warning',
  RETURNED_FOR_REVISION: 'warning',
  APPROVED: 'success',
  APPROVED_AS_NOTED: 'success',
  REJECTED: 'danger',
  CLOSED: 'neutral',
  VOID: 'neutral',
})

export const submittalVerdictVariant = lookup({
  APPROVED: 'success',
  APPROVED_AS_NOTED: 'success',
  REVISE_AND_RESUBMIT: 'warning',
  REJECTED: 'danger',
})

/**
 * Progress of one workflow step on a submittal revision. Not a Prisma enum —
 * these four states are derived from the step's review rows (see
 * SubmittalReviewsCard) — but they are drawn with the same tinted badge on the
 * step list AND on the read-only progress flow, so the mapping lives here
 * rather than being spelled out twice. `returned` is a step the engine
 * short-circuited on a negative verdict; it shares `danger` with an overdue
 * step, since both mean the plan stopped going forward.
 */
export const submittalWorkflowStepVariant = lookup({
  done: 'success',
  active: 'info',
  returned: 'danger',
  planned: 'neutral',
  // `skipped` shares `neutral` with `planned` — both mean "no verdict came from
  // this step". The label is what separates "still coming" from "never ran".
  skipped: 'neutral',
})

/**
 * Submittal activity log actions (SubmittalActivityAction in
 * src/graphql/submittals/activity.ts) — 12 literal values, not a large
 * substring-matched enum like Doc Control's activity log, so this is an exact
 * `lookup()` rather than `docActivityVariant`'s substring matcher. Grouped the
 * same way: creation/positive outcomes are `success`, removals/voids are
 * `danger`, in-between transitions are `warning`/`info`.
 */
export const submittalActivityVariant = lookup({
  CREATED: 'success',
  REVISION_CREATED: 'success',
  ATTACHMENT_ADDED: 'success',
  PUBLISHED: 'success',
  SUBMITTED: 'info',
  VERDICT: 'info',
  CONSOLIDATED: 'info',
  UPDATED: 'warning',
  REOPENED: 'warning',
  CLOSED: 'neutral',
  VOIDED: 'danger',
  ATTACHMENT_REMOVED: 'danger',
})

/** Schedule (Prisma ScheduleStatus). */
export const scheduleStatusVariant = lookup({ DRAFT: 'info', ACTIVE: 'success', ARCHIVED: 'neutral' })

/** Schedule activity health, derived from float/progress vs. status date — not a Prisma enum. */
export const scheduleHealthVariant = lookup({ COMPLETED: 'neutral', ON_TRACK: 'success', AT_RISK: 'warning', DELAYED: 'danger' })

/** Cost Control contract lifecycle: a draft is information, an active contract is live, a closed one is archived. */
export const costContractStatusVariant = lookup({
  DRAFT: 'info',
  ACTIVE: 'success',
  CLOSED: 'neutral',
})

/** Cost Control change lifecycle: waiting on someone is a warning, a decision is success or danger; a dismissed potential change is closed, not refused. */
export const costChangeStatusVariant = lookup({
  DRAFT: 'info',
  PENDING: 'warning',
  APPROVED: 'success',
  REJECTED: 'danger',
  DISMISSED: 'neutral',
})

/** Cost Control invoice lifecycle: approval pending is a warning, approved is information, paid is done. */
export const costInvoiceStatusVariant = lookup({
  PENDING: 'warning',
  APPROVED: 'info',
  PAID: 'success',
})

/** Cost Control payment application: a draft is information, submitted waits for the reviewer, certified is done. */
export const costApplicationStatusVariant = lookup({
  DRAFT: 'info',
  SUBMITTED: 'warning',
  CERTIFIED: 'success',
})

/** Cost Control RFQ (part D): an open request is information, an awarded one is done, a cancelled one is archived. */
export const costRfqStatusVariant = lookup({
  OPEN: 'info',
  AWARDED: 'success',
  CANCELLED: 'neutral',
})
